import { useCallback, useMemo, useState, useEffect } from "react";
import React from "react";
import { syncVisitorId } from "@/utils/visitorIdSync";
import { mutate } from "swr";
import {
  Cog6ToothIcon,
  CreditCardIcon,
  CheckCircleIcon,
  EnvelopeIcon,
  ChatBubbleLeftIcon,
  CurrencyDollarIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import useCurrentAccount from "@/components/context/useCurrentAccount";
import usePaymentMethods from "@/components/context/usePaymentMethods";
import CenteredModalDialog from "@/components/blocks/CenteredModalDialog";
import Link from "next/link";
import { trackPurchase } from "@/utils/analytics";
import {
  planRequiresPaymentMethod,
  planSupportsFreeTrial,
  paymentMethodRequiredForPlanChange,
} from "@/utils/billing";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { PageHeader } from "@/components/ui/page-header";

export type Subscription = {
  id: string;
  name: string;
  price: number;
  monitors: number;
  daily_emails: number;
};

export const allSubscriptions: Subscription[] = [
  { id: "free", name: "Free", price: 0, monitors: 10, daily_emails: 10 },
  { id: "10", name: "Pro 10", price: 495, monitors: 10, daily_emails: 10 },
  { id: "50", name: "Pro 50", price: 1495, monitors: 50, daily_emails: 50 },
  { id: "100", name: "Pro 100", price: 2495, monitors: 100, daily_emails: 100 },
  { id: "200", name: "Pro 200", price: 4995, monitors: 200, daily_emails: 200 },
  { id: "500", name: "Pro 500", price: 9995, monitors: 500, daily_emails: 500 },
  {
    id: "1000",
    name: "Pro 1000",
    price: 19995,
    monitors: 1000,
    daily_emails: 1000,
  },
];

export function subscriptionPlan(planId: string) {
  if (planId === "free") {
    return allSubscriptions[0];
  } else {
    const planMonitors = planId.split("-")[0];
    const subscription = allSubscriptions.find((s) => s.id === planMonitors);

    return subscription || allSubscriptions[0];
  }
}

function planName(planId: string) {
  if (planId === "free") {
    return "Free";
  } else {
    return `Pro (${planId.split("-")[0]} monitors)`;
  }
}

const emailPackages = [
  { email: 1000, price: 495 },
  { email: 3000, price: 995 },
  { email: 10000, price: 1995 },
];

const smsPackages = [
  { sms: 100, price: 1495 },
  { sms: 200, price: 2495 },
  { sms: 500, price: 5495 },
  { sms: 1000, price: 9995 },
];

export default function DashboardSettingsPage() {
  const { account } = useCurrentAccount({ requireLoggedIn: true });
  const {
    paymentMethods,
    isLoading: paymentMethodsLoading,
    isError: paymentMethodsError,
  } = usePaymentMethods();
  const [changePlanFormOpen, setChangePlanFormOpen] = useState(false);
  const [changePlanProcessing, setChangePlanProcessing] = useState(false);
  const [paymentMethodDeleting, setPaymentMethodDeleting] = useState(false);
  const [makingDefault, setMakingDefault] = useState(false);

  const [prepaidEmailFormOpen, setPrepaidEmailFormOpen] = useState(false);
  const [prepaidEmailProcessing, setPrepaidEmailProcessing] = useState(false);

  const [prepaidSmsFormOpen, setPrepaidSmsFormOpen] = useState(false);
  const [prepaidSmsProcessing, setPrepaidSmsProcessing] = useState(false);

  useEffect(() => {
    // Sync visitor ID when page loads
    syncVisitorId();
  }, []);

  const currentSubscription = useMemo(
    () => subscriptionPlan(account?.current_plan || ""),
    [account?.current_plan],
  );

  const addPaymentMethodClicked = useCallback(() => {
    if (changePlanProcessing) return;
    setChangePlanProcessing(true);

    fetch(`/web_api/billing/payment_methods`, {
      method: "POST",
    }).then((resp) => {
      if (resp.status === 200) {
        resp
          .json()
          .then((json) => {
            if (json.status === "ok") {
              if (json.url) {
                window.location.href = json.url;
              }
            } else {
              alert(json.error || "Error. Try again later.");
            }

            setChangePlanProcessing(false);
          })
          .catch((e) => {
            alert(e.toString() || "Error. Try again later.");
            setChangePlanProcessing(false);
          });
      } else {
        alert("Error. Try again later.");
        setChangePlanProcessing(false);
      }
    });
  }, [changePlanProcessing]);

  const changePlanBuyButtonClicked = useCallback(
    (planName: string, subscriptionTerm?: "monthly" | "annually") => {
      if (changePlanProcessing) return;

      setChangePlanProcessing(true);

      // Payment method check disabled - allowing plan changes without payment method
      // if (paymentMethods.length === 0 && planName !== "free") {
      //   alert("Let's add a payment method first.");
      //   setChangePlanProcessing(false);
      //   setTimeout(() => {
      //     addPaymentMethodClicked();
      //   }, 100);
      //   return;
      // }

      const planId = planName === "free" ? "free" : `${planName}-${subscriptionTerm}`;

      fetch(`/web_api/billing/change_subscription/${planId}`, {
        method: "POST",
      }).then((resp) => {
        if (resp.status === 200) {
          resp
            .json()
            .then((json) => {
              if (json.status === "subscription_changed") {
                // Track successful subscription purchase
                const subscription = subscriptionPlan(planName);
                if (subscription && planName !== "free" && subscription.price > 0) {
                  // Validate price is in cents before converting
                  if (typeof subscription.price === "number" && subscription.price > 0) {
                    const price = subscription.price / 100; // Convert cents to dollars
                    trackPurchase(price, "USD", subscription.name, subscriptionTerm || "monthly");
                  }
                }

                mutate("/web_api/accounts/current");
                setChangePlanFormOpen(false);
              } else if (json.status === "already_on_this_plan") {
                alert("You are already on this plan.");
                setChangePlanFormOpen(false);
              } else if (json.requires_payment_method) {
                alert(
                  "A payment method is required for this plan. Please add a payment method first.",
                );
                setChangePlanFormOpen(false);
                // Automatically open the payment method form
                setTimeout(() => {
                  addPaymentMethodClicked();
                }, 100);
              } else {
                alert(json.error || json.reason || "Error. Try again later.");
              }

              setChangePlanProcessing(false);
            })
            .catch((e) => {
              alert(e.toString() || "Error. Try again later.");
              setChangePlanProcessing(false);
            });
        } else {
          resp
            .json()
            .then((json) => {
              alert(json.error || json.reason || "Error. Try again later.");
            })
            .catch(() => {
              alert("Error. Try again later.");
            });
          setChangePlanProcessing(false);
        }
      });
    },
    [changePlanProcessing, addPaymentMethodClicked],
  );

  const deletePaymentMethodClicked = useCallback(
    (paymentMethodId: string) => {
      if (paymentMethodDeleting) return;

      setPaymentMethodDeleting(true);

      fetch(`/web_api/billing/payment_methods/${paymentMethodId}`, {
        method: "DELETE",
      }).then((resp) => {
        if (resp.status === 200) {
          mutate("/web_api/billing/payment_methods");
          mutate("/web_api/accounts/current");
          setPaymentMethodDeleting(false);
        } else {
          resp
            .json()
            .then((json) => {
              if (json.error) {
                alert("Error: " + json.error);
              } else {
                alert("Error. Try again later.");
              }
              setPaymentMethodDeleting(false);
            })
            .catch((_e) => {
              alert("Error. Try again later.");
              setPaymentMethodDeleting(false);
            });
        }
      });
    },
    [paymentMethodDeleting],
  );

  const makeDefaultPaymentMethodClicked = useCallback(
    (paymentMethodId: string) => {
      if (makingDefault) return;
      setMakingDefault(true);

      fetch(`/web_api/billing/payment_methods/${paymentMethodId}/make_default`, {
        method: "POST",
      })
        .then((resp) => {
          if (resp.status === 200) {
            mutate("/web_api/accounts/current");
            mutate("/web_api/billing/payment_methods");
          } else {
            resp
              .json()
              .then((json) => {
                alert(json.error || json.reason || "Error. Try again later.");
              })
              .catch(() => {
                alert("Error. Try again later.");
              });
          }
        })
        .finally(() => {
          setMakingDefault(false);
        });
    },
    [makingDefault],
  );

  const prepaidEmailBuyButtonClicked = useCallback(
    (emailCount: number) => {
      if (prepaidEmailProcessing) return;

      setPrepaidEmailProcessing(true);

      fetch(`/web_api/billing/create_checkout_session/email_${emailCount}`, {
        method: "POST",
      })
        .then((resp) => resp.json())
        .then((json) => {
          if (json.status === "ok") {
            if (json.url) {
              window.location.href = json.url;
            }
          } else {
            alert(json.error || "Error. Try again later.");
          }
        })
        .catch((e) => {
          alert(e.toString() || "Error. Try again later.");
        })
        .finally(() => {
          setPrepaidEmailProcessing(false);
        });
    },
    [prepaidEmailProcessing],
  );

  const prepaidSmsBuyButtonClicked = useCallback(
    (smsCount: number) => {
      if (prepaidSmsProcessing) return;

      setPrepaidSmsProcessing(true);

      fetch(`/web_api/billing/create_checkout_session/sms_${smsCount}`, {
        method: "POST",
      })
        .then((resp) => resp.json())
        .then((json) => {
          if (json.status === "ok") {
            if (json.url) {
              window.location.href = json.url;
            }
          } else {
            alert(json.error || "Error. Try again later.");
          }
        })
        .catch((e) => {
          alert(e.toString() || "Error. Try again later.");
        })
        .finally(() => {
          setPrepaidSmsProcessing(false);
        });
    },
    [prepaidSmsProcessing],
  );

  const subscriptionStatus = useMemo(() => {
    if (!account?.subscription) return <span>&nbsp;</span>;

    if (account.current_plan === "free") {
      return "";
    }

    if (account.subscription.status === "trialing") {
      const trialEnd = new Date(account.subscription.trial_end! * 1000);
      return `Trial ends on ${trialEnd.toLocaleDateString()}`;
    }

    if (account.subscription.status === "active") {
      const currentPeriodEnd = new Date(account.subscription.current_period_end! * 1000);
      return (
        <span>
          Next ${account.subscription.amount / 100} payment on{" "}
          {currentPeriodEnd.toLocaleDateString()}
        </span>
      );
    }

    if (account.subscription.status === "past_due") {
      return <span>Past due</span>;
    }

    if (account.subscription.status === "unpaid") {
      return <span>Unpaid</span>;
    }

    return account.subscription.status;
  }, [account?.subscription, account?.current_plan]);

  return (
    <DashboardLayout
      activePath="/dashboard/billing"
      title="Billing"
      breadcrumbs={[{ label: "Billing" }]}
    >
      <div className="mx-auto max-w-6xl px-4 py-8">
        <PageHeader title="Billing" description="Manage your subscription and payment methods" />

        {/* Main Content */}
        <div className="space-y-6">
          {/* Current Plan Section */}
          <Card>
            <CardHeader>
              <CardTitle>Subscription</CardTitle>
              <CardDescription>Your current plan and usage</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="space-y-1">
                  <p className="text-sm font-medium leading-none">Current Plan</p>
                  <p className="text-2xl font-bold" data-testid="current-plan-name">
                    {planName(account?.current_plan || "")}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">
                    Daily free emails limit: {currentSubscription.daily_emails}
                  </p>
                  {subscriptionStatus && (
                    <p className="text-sm text-muted-foreground">{subscriptionStatus}</p>
                  )}
                </div>
                <Button onClick={() => setChangePlanFormOpen(true)} disabled={changePlanProcessing}>
                  Change plan
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Payment Methods Section */}
          <Card>
            <CardHeader>
              <CardTitle>Payment Methods</CardTitle>
              <CardDescription>Manage your payment methods</CardDescription>
            </CardHeader>
            <CardContent>
              {paymentMethodsLoading ? (
                <div className="text-muted-foreground">Loading payment methods...</div>
              ) : paymentMethodsError ? (
                <div className="text-destructive">Cannot load payment methods.</div>
              ) : paymentMethods.length === 0 ? (
                <div className="text-muted-foreground mb-4">No payment methods added yet.</div>
              ) : (
                <div className="space-y-3 mb-6">
                  {paymentMethods.map((paymentMethod) => (
                    <div
                      key={paymentMethod._id}
                      className="flex items-center justify-between p-4 border rounded-lg"
                      data-testid="payment-method-card"
                      data-payment-method-id={paymentMethod._id}
                      data-card-type={`${paymentMethod.card_brand.toLowerCase()}-${paymentMethod.last4}`}
                      data-is-default={account?.current_payment_method === paymentMethod._id}
                    >
                      <div className="flex items-center gap-3">
                        <CreditCardIcon className="h-5 w-5 text-muted-foreground" />
                        <div>
                          <p className="font-medium">
                            {paymentMethod.card_brand} ending in {paymentMethod.last4}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            Expires {paymentMethod.exp_month}/{paymentMethod.exp_year}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {account?.current_payment_method === paymentMethod._id ? (
                          <>
                            <Badge variant="secondary">Default</Badge>
                            {account?.current_plan === "free" && (
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => deletePaymentMethodClicked(paymentMethod._id)}
                                disabled={paymentMethodDeleting}
                              >
                                Delete
                                {paymentMethodDeleting && (
                                  <Cog6ToothIcon className="ml-2 animate-spin h-4 w-4" />
                                )}
                              </Button>
                            )}
                          </>
                        ) : (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => makeDefaultPaymentMethodClicked(paymentMethod._id)}
                              disabled={makingDefault}
                            >
                              Make Default
                              {makingDefault && (
                                <Cog6ToothIcon className="ml-2 animate-spin h-4 w-4" />
                              )}
                            </Button>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => deletePaymentMethodClicked(paymentMethod._id)}
                              disabled={paymentMethodDeleting}
                            >
                              Delete
                              {paymentMethodDeleting && (
                                <Cog6ToothIcon className="ml-2 animate-spin h-4 w-4" />
                              )}
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <Button onClick={addPaymentMethodClicked} disabled={changePlanProcessing}>
                <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
                Add payment method
              </Button>
            </CardContent>
          </Card>

          {/* Prepaid Credits Section */}
          <div className="grid gap-6 md:grid-cols-2">
            {/* Email Credits */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <EnvelopeIcon className="h-5 w-5 text-muted-foreground" />
                  <CardTitle>Email Credits</CardTitle>
                </div>
                <CardDescription>Additional email notifications</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <p className="text-3xl font-bold">{account?.prepaid_email_messages || 0}</p>
                    <p className="text-sm text-muted-foreground">messages remaining</p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => setPrepaidEmailFormOpen(true)}
                    data-testid="buy-email-button"
                  >
                    Buy email package
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* SMS Credits */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <ChatBubbleLeftIcon className="h-5 w-5 text-muted-foreground" />
                  <CardTitle>SMS Credits</CardTitle>
                </div>
                <CardDescription>Text message notifications</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <p className="text-3xl font-bold">{account?.prepaid_sms_messages || 0}</p>
                    <p className="text-sm text-muted-foreground">messages remaining</p>
                  </div>
                  <Button variant="outline" onClick={() => setPrepaidSmsFormOpen(true)}>
                    Buy SMS package
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <CenteredModalDialog
        show={changePlanFormOpen}
        onClose={() => setChangePlanFormOpen(false)}
        className="!max-w-3xl"
      >
        <div className="bg-card shadow-xl rounded-lg w-full">
          <div className="px-6 py-4 border-b border-border">
            <h3 className="text-2xl font-semibold text-foreground">Change Subscription Plan</h3>
          </div>

          <div className="p-6">
            <h4 className="text-xl font-medium text-foreground mb-6 text-center">
              Uptime Monitor Pro
            </h4>

            <div className="space-y-4">
              {allSubscriptions
                .filter((s) => s.id !== "free")
                .map((subscription) => (
                  <div
                    key={subscription.id}
                    className="bg-card rounded-lg p-4 border border-border"
                    data-testid={`subscription-plan-${subscription.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h5 className="text-lg font-medium text-foreground">
                          {subscription.name}
                          {planSupportsFreeTrial(subscription.id) && !account?.trial_used && (
                            <Badge variant="secondary" className="ml-2">
                              7-day free trial
                            </Badge>
                          )}
                        </h5>
                        <p className="text-sm text-muted-foreground">
                          {subscription.monitors} monitors • {subscription.daily_emails} daily
                          emails
                        </p>
                      </div>
                      <div className="flex gap-2">
                        {/* Monthly subscription button */}
                        <Button
                          variant={
                            planSupportsFreeTrial(subscription.id) && !account?.trial_used
                              ? "default"
                              : "secondary"
                          }
                          onClick={() => changePlanBuyButtonClicked(subscription.id, "monthly")}
                          disabled={
                            changePlanProcessing ||
                            (paymentMethodRequiredForPlanChange(
                              subscription.id,
                              account?.trial_used || false,
                            ) &&
                              (!paymentMethods || paymentMethods.length === 0))
                          }
                          data-testid={`subscribe-monthly-${subscription.id}`}
                        >
                          {planSupportsFreeTrial(subscription.id) && !account?.trial_used
                            ? "Start 7-day free trial"
                            : `$${subscription.price / 100}/month`}
                        </Button>

                        {/* Annual subscription button */}
                        <Button
                          onClick={() => changePlanBuyButtonClicked(subscription.id, "annually")}
                          disabled={
                            changePlanProcessing ||
                            (paymentMethodRequiredForPlanChange(
                              `${subscription.id}-annually`,
                              account?.trial_used || false,
                            ) &&
                              (!paymentMethods || paymentMethods.length === 0))
                          }
                          data-testid={`subscribe-annually-${subscription.id}`}
                        >
                          ${subscription.price / 10}0/year
                          <Badge variant="secondary" className="ml-2">
                            2 months free
                          </Badge>
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}

              {/* Free Plan */}
              <div className="bg-card rounded-lg p-4 border border-border">
                <div className="flex items-center justify-between">
                  <div>
                    <h5 className="text-lg font-medium text-foreground">Free Plan</h5>
                    <p className="text-sm text-muted-foreground">10 monitors • 10 daily emails</p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => changePlanBuyButtonClicked("free")}
                    disabled={changePlanProcessing}
                  >
                    Switch to Free
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </CenteredModalDialog>

      <CenteredModalDialog
        show={prepaidEmailFormOpen}
        onClose={() => setPrepaidEmailFormOpen(false)}
        className="!max-w-lg"
      >
        <div className="bg-card shadow-xl rounded-lg w-full">
          <div className="px-6 py-4 border-b border-border">
            <h3 className="text-2xl font-semibold text-foreground flex items-center">
              <EnvelopeIcon className="h-6 w-6 mr-2 text-blue-500" />
              Buy Email Package
            </h3>
          </div>

          <div className="p-6">
            <p className="text-muted-foreground mb-6 text-center">
              Purchase email credits for additional notifications beyond your plan&apos;s daily
              limit
            </p>
            <div className="space-y-4">
              {emailPackages.map((emailPackage) => (
                <div
                  key={`email-${emailPackage.email}`}
                  className="bg-card rounded-lg p-4 border border-border hover:border-muted-foreground transition-colors"
                  data-testid={`email-package-${emailPackage.email}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h5 className="text-lg font-medium text-foreground">
                        {emailPackage.email.toLocaleString()} emails
                      </h5>
                      <p className="text-sm text-muted-foreground">
                        ${((emailPackage.price / 100 / emailPackage.email) * 1000).toFixed(2)} per
                        1,000 emails
                      </p>
                    </div>
                    <Button
                      onClick={() => prepaidEmailBuyButtonClicked(emailPackage.email)}
                      disabled={prepaidEmailProcessing}
                      data-testid={`buy-email-package-${emailPackage.email}`}
                    >
                      ${emailPackage.price / 100}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </CenteredModalDialog>

      <CenteredModalDialog
        show={prepaidSmsFormOpen}
        onClose={() => setPrepaidSmsFormOpen(false)}
        className="!max-w-lg"
      >
        <div className="bg-card shadow-xl rounded-lg w-full">
          <div className="px-6 py-4 border-b border-border">
            <h3 className="text-2xl font-semibold text-foreground flex items-center">
              <ChatBubbleLeftIcon className="h-6 w-6 mr-2 text-blue-500" />
              Buy SMS Package
            </h3>
          </div>

          <div className="p-6">
            <p className="text-muted-foreground mb-6 text-center">
              Purchase SMS credits for text message notifications
            </p>
            <div className="space-y-4">
              {smsPackages.map((smsPackage) => (
                <div
                  key={`sms-${smsPackage.sms}`}
                  className="bg-card rounded-lg p-4 border border-border hover:border-muted-foreground transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h5 className="text-lg font-medium text-foreground">
                        {smsPackage.sms.toLocaleString()} SMS
                      </h5>
                      <p className="text-sm text-muted-foreground">
                        ${(smsPackage.price / 100 / smsPackage.sms).toFixed(2)} per SMS
                      </p>
                    </div>
                    <Button
                      onClick={() => prepaidSmsBuyButtonClicked(smsPackage.sms)}
                      disabled={prepaidSmsProcessing}
                    >
                      ${smsPackage.price / 100}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </CenteredModalDialog>
    </DashboardLayout>
  );
}
